import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ProjectSummaryScreen from "../../screen/ProjectSummaryScreen"
import { useScope } from "../SelectProjectCompanyContainer"
import { customToast } from "../../components/Toast"
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query"
import { useTechnicianUsers, useUserWithDateFilter } from "../../core/utils/hooks/useUsers"
import { useSelector } from "react-redux"
import moment from "moment"
import {
  getTestimonials,
  getTestimonialsFavorited,
  testimonialsFilter,
  updateFavoriteTestimonial,
} from "../../core/services/testimonials.service"
import { getBeneficiaryJourney } from "../../core/services/getBeneficiary.service"
import { getLevelInteraction } from "../../core/services/getLevelInteraction.service"
import {
  getAssistanceEvaluation,
  getAssistanceEvaluationHistory,
  getDashboardEvaluation,
} from "../../core/services/getAssistanceEvaluation.service"
import { getContentsSent } from "../../core/services/getContentSent.service"
import { getAssistancesByTec } from "../../core/services/getAssistances.service"
import {
  findNode,
  formatProjectTreeSelect,
  getProjectIds,
  getProjectIdsFromTree,
} from "../../core/utils/projectsTreeSelect"
import { isObjectEffectivelyEmpty } from "../../components/NewChatComponents"
import { isEqual } from "lodash"


const fetchQuery = (key, fetchFn, enabled = true, keepCache = true, notifyOnChangeProps = null) =>
  useQuery(key, fetchFn, {
    enabled,
    staleTime: keepCache ? 900000 : undefined,
    cacheTime: keepCache ? 900000 : undefined,
    notifyOnChangeProps: notifyOnChangeProps,
    onError: () => { },
  })

const getQueries = (
  scope,
  scopeType,
  favoritedTestimonials,
  favoriteIndex,
  technicianSelect,
  projectsPeriodFilter,
  user,
  currentUserRole,
  usersProjectFetched,
  usersProject,
  projectsByScope,
  assistancesDetailsFilters
) => {
  const queries = {
    beneficiaryJourney: fetchQuery(["beneficiaryJourney", scope?.id, usersProjectFetched], () =>
      getBeneficiaryJourney(scope?.id, scopeType, currentUserRole, usersProject),
    ),

    assistanceDetails: fetchQuery(["assistanceDetails", scope?.id], () =>
      getLevelInteraction(scope?.id, scopeType, user, currentUserRole),
    ),

    assistanceEvaluation: fetchQuery(
      ["assistanceEvaluation", scope?.id, projectsPeriodFilter, technicianSelect],
      () =>
        getAssistanceEvaluation(
          user,
          scope,
          scopeType,
          technicianSelect,
          currentUserRole,
          projectsPeriodFilter,
        ),
    ),

    dashboardEvaluation: fetchQuery(
      ["dashboardEvaluation", scope?.id, projectsPeriodFilter, technicianSelect],
      () =>
        getDashboardEvaluation(
          user,
          scope,
          scopeType,
          technicianSelect,
          currentUserRole,
          projectsPeriodFilter,
        ),
    ),

    assistanceEvaluationHistory: fetchQuery(
      ["assistanceEvaluationHistory", scope?.id, technicianSelect],
      () =>
        getAssistanceEvaluationHistory(
          user,
          scope,
          scopeType,
          technicianSelect,
          currentUserRole,
          `${moment().format("YYYY-MM-DD")}|${moment().format("YYYY-MM-DD")}`,
        ),
    ),

    contentsSent: fetchQuery(["contentsSent", scope?.id], () =>
      getContentsSent(user, scope, scopeType, currentUserRole),
    ),

    assistanceData: fetchQuery(
      ["assistanceData", scope?.id, projectsPeriodFilter, technicianSelect],
      () => getAssistancesByTec(
        user,
        projectsByScope.map((project) => `'${project.id}'`).join(", "),
        scopeType,
        technicianSelect,
        currentUserRole,
        projectsPeriodFilter,
      ),
    ),
  }

  return queries
}

const assistancesTypes = [
  {
    label: "Atendimento Finalizado",
    value: "1",
  },
  {
    label: "Repasse p/ sí mesmo",
    value: "2",
  },
  {
    label: "Repasse p/ outros",
    value: "3",
  }
]

const ProjectSummaryContainer = () => {
  const { scope, scopeType, projectsByScope } = useScope()
  const [projectIds, setProjectIds] = useState(undefined)
  const user = useSelector((state) => state.app?.user?.user)
  const currentUserRole = useSelector((state) => state.app.currentUserRole)
  const organizations = useSelector((state) => state.app.organizations)
  const queryClient = useQueryClient()
  const [favoritedTestimonials, setFavoritedTestimonials] = useState(false)
  const [isFakeLoading, setIsFakeLoading] = useState(false)
  const [openModal, setOpenModal] = useState(null)
  const [currentTab, setCurrentTab] = useState("open")
  const [projectsPeriodFilter, setProjectsPeriodFilter] = useState(
    `2018-01-01|${moment().format("YYYY-MM-DD")}`,
  )
  const { users: usersProject, isFetched: usersProjectFetched } = useUserWithDateFilter()
  const technicalList = useTechnicianUsers()
  const [technicianSelect, setTechnicianSelect] = useState({})
  const [valueTree, setValueTree] = useState(undefined)
  const treeData = formatProjectTreeSelect(organizations)
  const [dateFilter, setDateFilter] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const startPageFilters = useSelector((state) => state.app?.startPageFilters) || null
  const [assistancesDetailsFilters, setAssistancesDetailsFilters] = useState({})

  const [favoriteIndex, setFavoriteIndex] = useState(false)

  const isUserManager = currentUserRole === "USER_MANAGER"
  const isUserAdmin = currentUserRole === "USER_ADMIN"
  const isUserTec = currentUserRole === "USER_TEC"

  const projectsOptions = scopeType === "organization" || scopeType === "company" && projectsByScope.length > 1 ? projectsByScope.map((project) => ({ label: project.description, value: project.id })) : []

  const handleFavoritedTestimonials = () => {
    setIsFakeLoading(true)
    setTimeout(() => {
      setFavoritedTestimonials((prev) => !prev)
      setIsFakeLoading(false)
    }, 100)
  }

  const { mutate: mutateFavorite } = useMutation(updateFavoriteTestimonial, {
    onSuccess: () => {
      queryClient.invalidateQueries(["favoriteTestimonials", scope?.id]) // Refetch
    },
    onError: () => {
      customToast("Erro ao Salvar Destaque", "error")
    },
  })

  const handleFavoriteTestimonial = (testimonial, index) => {

    const queryKey = ["favoriteTestimonials", scope?.id]
    const previousData = queryClient.getQueryData(queryKey)

    if (!previousData) {
      return
    }

    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData || !Array.isArray(oldData)) return oldData

      return oldData.map((item, i) =>
        i === index ? { ...item, value: testimonial.value } : item
      )
    })

    mutateFavorite(testimonial, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey)
        customToast(
          testimonial.value === "Sim"
            ? "Destaque salvo com sucesso"
            : "Destaque removido com sucesso",
          "success"
        )
      },
      onError: (error) => {
        queryClient.setQueryData(queryKey, previousData)
        customToast("Erro ao concluir ação", "error")
      },
    })
  }


  const toggleModal = useCallback((value) => {
    setOpenModal((prev) => (prev === value ? null : value))
    setCurrentTab(value === "sharedContent" ? "contents" : null)
  }, [])

  useEffect(() => {
    if (!treeData.length || !scope?.id || currentUserRole !== "USER_MANAGER") return

    if (!valueTree) {
      setValueTree(scope?.id)
    }

    let selectedIds = []

    if (!valueTree || valueTree === scope?.id) {
      selectedIds = getProjectIds(treeData, scope?.id, scopeType)
    } else {
      selectedIds = Array.isArray(valueTree)
        ? valueTree.flatMap((id) => getProjectIdsFromTree(treeData, id))
        : getProjectIdsFromTree(treeData, valueTree)
    }

    setProjectIds(selectedIds.map((value) => `'${value}'`).join(", "))
  }, [treeData, scope?.id, scopeType, valueTree, currentUserRole])


  const handleChangeTab = (newTab) => {
    if (Array.isArray(newTab)) {
      setCurrentTab(newTab[0]?.type)
    }
    else {
      setCurrentTab(newTab?.type)
    }
  }

  const handleProjectsPeriodFilter = (value) => {
    if (value) {
      setProjectsPeriodFilter(value)
    } else {
      setProjectsPeriodFilter(`2018-01-01|${moment().format("YYYY-MM-DD")}`)
    }
  }

  const handleChangeTecnhicianSelect = useCallback((value) => {
    setTechnicianSelect(value)
  }, [])

  const getAllChildrenValues = (node) => {
    if (!node.children) return [node.value]
    return node.children.flatMap(getAllChildrenValues)
  }

  const handleChangeTree = (selectedValue) => {
    const selectedNode = findNode(treeData, selectedValue)

    if (!selectedNode) return

    setValueTree(selectedValue)
    const values = Array.isArray(selectedValue) ? selectedValue : [selectedValue]
    setProjectIds(
      values
        .map((value) => value)
        .map((value) => `'${value}'`)
        .join(", "),
    )
  }

  const {
    data: testimonials,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isFetching,
    isPreviousData,
    isLoading: isTestimonialsLoading,
  } = useInfiniteQuery({
    queryKey: ["testimonials", scope?.id],
    queryFn: ({ pageParam = 1 }) =>
      getTestimonials(
        scope?.id,
        scopeType,
        false,
        user,
        currentUserRole,
        usersProject,
        pageParam,
        10,
      ),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchOnWindowFocus: true,
    notifyOnChangeProps: ['isFetching']
  })

  const { data: favoriteTestimonials, isLoading: isFavoritesLoading, isRefetching: isFavoriteRefetching } = useQuery({
    queryKey: ["favoriteTestimonials", scope?.id],
    queryFn: () =>
      getTestimonialsFavorited(scope?.id, scopeType, true, user, currentUserRole, usersProject),
    refetchOnWindowFocus: true,
  })

  const allTestimonials = useMemo(() => {
    const regularTestimonials = testimonials?.pages?.flatMap((page) => page.data) || []

    if (favoriteTestimonials?.length > 0) {
      return [...favoriteTestimonials, ...regularTestimonials]
    }
    return regularTestimonials
  }, [testimonials, favoriteTestimonials])

  const testimonialsData = useMemo(() => {
    setFavoriteIndex(false)
    return testimonialsFilter(allTestimonials, favoritedTestimonials)
  }, [allTestimonials, favoritedTestimonials])


  const onFilterDate = (value) => {
    setDateFilter(value)
  }


  const handleShowFilters = () => {
    setShowFilters((prev) => !prev)
  }

  const queries = getQueries(
    scope,
    scopeType,
    favoritedTestimonials,
    favoriteIndex,
    technicianSelect,
    projectsPeriodFilter,
    user,
    currentUserRole,
    usersProjectFetched,
    usersProject,
    projectsByScope,
    assistancesDetailsFilters,
  )

  const handleAssistancesDetailsFilters = (field, value) => {
    setAssistancesDetailsFilters((prev) => ({
      ...prev,
      [field]: value,
    }))

  }



  const { data: beneficiaryJourney } = queries.beneficiaryJourney
  const { data: assistanceDetails } = queries.assistanceDetails
  const { data: dashboardEvaluation } = queries.dashboardEvaluation
  const { data: assistanceEvaluationHistory } = queries.assistanceEvaluationHistory
  const { data: contentsSent } = queries.contentsSent
  const { data: assistanceData } = queries.assistanceData

  const previousData = useRef([]);

  /* useEffect(() => {
    if (!isEqual(previousData.current, data)) {
      previousData.current = data;

      // Resetar cache de altura e recomputar
      cache.clearAll();
      listRef.current?.recomputeRowHeights();
    }
  }, [data]); */

  return (
    <ProjectSummaryScreen
      handleFavoritedTestimonials={handleFavoritedTestimonials}
      handleProjectsPeriodFilter={handleProjectsPeriodFilter}
      projectsPeriodFilter={projectsPeriodFilter}
      favoritedTestimonials={favoritedTestimonials}
      handleFavoriteTestimonial={handleFavoriteTestimonial}
      isLoading={isTestimonialsLoading || isFakeLoading || isFavoritesLoading}
      isFavoriteRefetching={isFavoriteRefetching}
      testimonials={testimonialsData}
      bestESMcollected={[]}
      beneficiaryJourney={beneficiaryJourney}
      beneficiaries={usersProject}
      toggleModal={toggleModal}
      openModal={openModal}
      handleChangeTab={handleChangeTab}
      currentTab={currentTab}
      currentUser={currentUserRole}
      assistanceDetails={assistanceDetails}
      contents={filterContents(contentsSent, dateFilter)?.content || []}
      producers={filterContents(contentsSent, dateFilter)?.beneficiary || []}
      handleChangeTree={handleChangeTree}
      valueTree={valueTree}
      treeData={treeData}
      handleChangeTecnhicianSelect={handleChangeTecnhicianSelect}
      technicianOptions={technicalList}
      dashboardEvaluation={dashboardEvaluation}
      assistanceEvaluationHistory={assistanceEvaluationHistory}
      isUserAdmin={isUserAdmin}
      isUserTec={isUserTec}
      isUserManager={isUserManager}
      projectId={scope?.id}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isRefetching={isFetching}
      onFilterDate={onFilterDate}
      dateFilter={dateFilter}
      assistanceData={assistanceData}
      handleShowFilters={handleShowFilters}
      showFilters={showFilters}
      assistancesTypes={assistancesTypes}
      handleAssistancesDetailsFilters={handleAssistancesDetailsFilters}
      assistancesDetailsFilters={assistancesDetailsFilters}
      allDetailsAssistancesTechnicians={mapToSelectOptions(assistanceDetails?.allAssistances, "nomeResponsavel", "idResponsavel")}
      allDetailsAssistancesUsers={mapToSelectOptions(assistanceDetails?.allAssistances, "NomeProdutor", "idprodutor")}
      allDetailsAssistances={filterDetailedAssistances(assistanceDetails?.allAssistances, assistancesDetailsFilters)}
      projectsOptions={projectsOptions}
    />
  )
}

export default ProjectSummaryContainer

const mapToSelectOptions = (items = [], labelKey, valueKey) => {
  const seen = new Set();
  const result = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const label = String(item[labelKey]);
    const value = String(item[valueKey]);

    if (!seen.has(value)) {
      seen.add(value);
      result.push({ label, value });
    }
  }

  return result;
}

export const filterDetailedAssistances = (data = [], filters = {}) => {
  let filtered = [...data]


  if (filters.responsible?.value) {
    filtered = filtered.filter(item =>
      item.idResponsavel === filters.responsible.value
    )
  }

  if (filters.producers?.value) {
    filtered = filtered.filter(item =>
      item.idprodutor === filters.producers.value
    )
  }

  if (filters.assistanceType?.label) {
    filtered = filtered.filter(item =>
      item.acao.toLowerCase() === filters.assistanceType?.label.toLowerCase()
    )
  }

  if (Array.isArray(filters?.projects) && filters.projects.length > 0) {
    const selectedProjectIds = filters.projects.map(project => project?.value).filter(Boolean)

    filtered = filtered.filter(item => selectedProjectIds.includes(item.project))
  }

  if (filters.date && filters.date.includes('|')) {
    const [startDateStr, endDateStr] = filters.date.split('|').map(s => s.trim())

    filtered = filtered.filter(item => {
      return item.data.value >= startDateStr && item.data.value <= endDateStr
    })
  }

  return filtered
}

const filterContents = (contents, date = null) => {
  let aux = { content: [], beneficiary: [] }

  if (contents?.content) {
    aux = { ...aux, content: contents.content }
  }

  if (contents?.beneficiary) {
    aux = { ...aux, beneficiary: contents.beneficiary }
  }

  if (date) {
    const [startDate, endDate] = date.split("|").map((d) => new Date(d))

    aux.content = aux.content
      .map((item) => {
        const filteredData = item.data.filter((dataItem) => {
          const sendDate = new Date(dataItem.send_data)
          return sendDate >= startDate && sendDate <= endDate
        })

        if (filteredData.length > 0) {
          return {
            ...item,
            data: filteredData,
            totalBeneficiaries: filteredData.length,
          }
        }

        return false
      })
      .filter((item) => item)

    aux.beneficiary = aux.beneficiary
      .map((item) => {
        const filteredData = item.data.filter((dataItem) => {
          const sendDate = new Date(dataItem.send_data)
          return sendDate >= startDate && sendDate <= endDate
        })

        if (filteredData.length > 0) {
          return {
            ...item,
            data: filteredData,
            totalContents: filteredData.length,
          }
        }

        return false
      })
      .filter((item) => item)
  }

  return aux
}
